import React, { useCallback } from 'react';
import styled from 'styled-components';
import { message } from 'antd';

import Typography from 'antd/es/typography';
import Modal from 'antd/es/modal';

import colors from 'config/colors';
import useModal from 'hooks/useModal';

import useResource from 'hooks/useResource';

import { ResourceType } from 'services/resources';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import { Row, Col } from 'components/layout';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import EmployeeForm from '../components/forms/Employee.form';
import { EditAction, DeleteAction, DownloadAction } from '../components/TableActions';
import EmployeeInfo from '../components/EmployeeInfo';

import { mapLocationsToOptions, mapAgenciesToOptions } from 'helpers/mapToOptions';
import { saveFile } from 'helpers/saveFile';

const download = (imgUrl, empName) => {
  fetch(imgUrl, {
    method: "GET",
    headers: {}
  })
    .then(response => {
      response.arrayBuffer().then(function (buffer) {
        let blob = new Blob([buffer])
        saveFile(blob, `${empName}.png`)
      });
    })
    .catch(err => {
      console.log(err);
    });
};

const EmployeesPage = () => {
  const infoModal = useModal();
  const locations = useResource(ResourceType.locations, { prefetch: true });
  const agencies = useResource(ResourceType.agencies, { prefetch: true });

  const employees = useAdminResource({
    resource: ResourceType.employees,
    resourceName: 'employee',
    searchFields: [
      'employee_id',
      'full_name'
    ],
  });

  const handleDelete = async (employee_id) => {
    try {
      await employees.onDelete(employee_id);
      message.success('Employee deleted successfully!', 5);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.Error || 'Failed to delete the employee.';
      message.error(errorMessage, 10);
    }
  };

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        employees.onEdit(item);
        break
      case DeleteAction.actionType:
        handleDelete(item.employee_id);
        break;
      case DownloadAction.actionType:
        download(item.tag_img, item.full_name);
        break;
      default: // make eslint happy :)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  return (
    <AdminLayout
      title="Employees"
      icon="users"
      extra={[
        <Search
          placeholder={employees.strings.searchPlaceholder}
          value={employees.searchVal}
          onChange={value => employees.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            employees.onCreate({ is_active: 1 });
          }}
        >
          {employees.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={employees.strings.formTitle}
        form={employees.form}
        submitText={employees.strings.formSubmitButton}
        onClose={() => employees.form.reset()}
        disabledButtons={employees.form.isSubmitting}
      >
        <EmployeeForm
          form={employees.form}
          locationOptions={mapLocationsToOptions(locations.state.list)}
          agencyOptions={mapAgenciesToOptions(agencies.state.list)}
        />
      </FormModal>
      <Table
        loading={employees.isLoading || locations.state.isLoading || agencies.state.isLoading}
        dataSource={employees.list}
        actions={[
          EditAction,
          DeleteAction,
          DownloadAction
        ]}
        actionsProps={{
          resource: 'employee',
        }}
        onAction={onAction}
        rowKey="employee_id"
      >
        <Table.Column title="ID" dataIndex="employee_id" />
        <Table.Column title="Name" dataIndex="full_name"
          render={fullName => (
            <Typography.Link as="span" style={{ color: colors.primary }}>
              {fullName}
            </Typography.Link>
          )}
          onCell={item => {
            return {
              onClick: () => {
                infoModal.show(item);
              }
            };
          }
          } />
        <Table.Column title="Location" dataIndex="location_id" render={locationId => {
          const location = locations.state.list.find(l => l.location_id === locationId);
          if (!location) return '-';
          else return location.location_name;
        }} />
        <Table.Column title="Agency" dataIndex="agency_id" render={agencyId => {
          const agency = agencies.state.list.find(a => a.agency_id === agencyId);
          if (!agency) return '-';
          else return agency.name;
        }} />
        <Table.Column title="Notes" dataIndex="notes" />
      </Table>

      <Modal
        width={700}
        title={'Employee Detail'}
        visible={infoModal.isVisible}
        onCancel={infoModal.hide}
        footer={
          <>
            <Button
              icon="edit"
              onClick={() => {
                infoModal.hide();
                employees.onEdit(infoModal.data);
              }}
            >
              Edit
            </Button>
            <Button
              onClick={() => {
                infoModal.hide();
              }}
            >
              Close
            </Button>
          </>
        }
      >
        <Row style={{ padding: '0 24px' }}>
          <Col span={10}>
            {infoModal.data ? (
              <>
                <Row>
                  <Col span={24}>
                    <ArucoCodeImg alt="aruco_code" src={infoModal.data.tag_img} />
                    <ArcuoCodeName>{infoModal.data.full_name}</ArcuoCodeName>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <Button type="primary"
                      style={{
                        width: '188px',
                      }}
                      icon={{
                        name: 'download',
                        size: 16,
                      }}
                      onClick={() => {
                        onAction(DownloadAction.actionType, infoModal.data);
                      }}
                    >
                      Download code
                    </Button>
                  </Col>
                </Row>
              </>
            ) : null}
          </Col>
          <Col span={14}>
            <EmployeeInfo employee={infoModal.data} />
          </Col>
        </Row>
      </Modal>
    </AdminLayout>
  );
};

const ArucoCodeImg = styled.img`
  max-width: 100%;
  width: 240px;
  height: 240px;
  transform: translate(-10%, -8%);
`;

const ArcuoCodeName = styled.div`
  background-color: #FAFBFC;
  height: 26px;
  border: 1px solid #bdbdbd;
  width: 188px;
  transform: translate(2px, -32px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default EmployeesPage;
